"use client";

var __jsx = React.createElement;
import React, { useEffect, useState } from "react";
import MuscleModal from "../../../../shared/components/modal";
import { useTranslations } from "next-intl";
import MuscleSelectInput from "../../../../shared/components/select-input";
import { MuscleButton } from "../../../../shared/components/buttons";
import { useLanguageConfig } from "@lib/context/shared/lang-context";
import { useCustomer } from "@lib/context/shared/customer-context";
import customersManager from "@lib/data/managers/shared/customers-manager";
import { useNotifications } from "@lib/context/shared/notifications-context";
import { NotificationType } from "types/shared/notification-types";
import { APP_CURRENCIES, DEFAULT_CURRENCY } from "@lib/constants";
import { MIXPANEL_EVENTS } from "@lib/mixpanel/mixpanel.event-list";
import useTrackEvent from "@lib/hooks/use-track-event";
import APP_CONFIGURATION from "@lib/app.config";
const LanguageCurrencyConfigModal = _ref => {
  let {
    isOpen,
    setIsOpen
  } = _ref;
  const t = useTranslations("Shared.LangCurrencyConfig");
  const {
    getLanguageOptions,
    changePageLocale,
    currentLanguageCode,
    changeLanguageStates
  } = useLanguageConfig();
  const {
    customerCurrency,
    updateCustomerStates
  } = useCustomer();
  const {
    notify
  } = useNotifications();
  const {
    0: languageSelection,
    1: setLanguageSelection
  } = useState(currentLanguageCode);
  const {
    0: currencyOptions,
    1: setCurrencyOptions
  } = useState([]);
  const {
    0: currencySelection,
    1: setCurrencySelection
  } = useState(customerCurrency || process.env.NEXT_PUBLIC_DEFAULT_CURRENCY || DEFAULT_CURRENCY);
  const {
    modal_title_class
  } = APP_CONFIGURATION.colors.globals.modal;
  const {
    0: changedCurrency,
    1: setChangedCurrency
  } = useState(false);
  const {
    0: changedLanguage,
    1: setChangedLanguage
  } = useState(false);
  const {
    0: isLanguageDropdownOpen,
    1: setIsLanguageDropdownOpen
  } = useState(false);
  const {
    0: isCurrencyDropdownOpen,
    1: setIsCurrencyDropdownOpen
  } = useState(false);
  const {
    0: isUpdatingUserData,
    1: setIsUpdatingUserData
  } = useState(false);
  const trackEvent = useTrackEvent();
  useEffect(() => {
    var _ref2, _process$env$NEXT_PUB;
    const raw = (_ref2 = (_process$env$NEXT_PUB = process.env.NEXT_PUBLIC_CURRENCIES) !== null && _process$env$NEXT_PUB !== void 0 ? _process$env$NEXT_PUB : process.env.NEXT_PUBLIC_CURRENCYS) !== null && _ref2 !== void 0 ? _ref2 : "";
    const allowedCodes = raw.split(",").map(c => c.trim().toUpperCase()).filter(Boolean);
    let filtered = allowedCodes.length > 0 ? APP_CURRENCIES.filter(c => allowedCodes.includes(c.currency_code)) : APP_CURRENCIES;
    if (filtered.length === 0) filtered = APP_CURRENCIES; // invalid codes -> fallback

    setCurrencyOptions(parseCurrenciesToOptions(filtered));
    // Clamp selection to allowed set
    if (customerCurrency && filtered.some(c => c.currency_code === customerCurrency)) {
      setCurrencySelection(customerCurrency);
    } else if (!customerCurrency) {
      var _filtered$find;
      const fallback = (_filtered$find = filtered.find(c => c.currency_code === (process.env.NEXT_PUBLIC_DEFAULT_CURRENCY || DEFAULT_CURRENCY))) !== null && _filtered$find !== void 0 ? _filtered$find : filtered[0];
      setCurrencySelection(fallback.currency_code);
    }
  }, [customerCurrency, currentLanguageCode]);
  const parseCurrenciesToOptions = currencies => currencies.map(currency => ({
    key: currency.currency_code,
    value: "".concat(t(currency.currency_code), " (").concat(currency.currency_code, ")")
  }));
  const changeLanguage = languageCode => {
    setLanguageSelection(languageCode);
    changeLanguageStates(languageCode);
    setChangedLanguage(true);
  };
  const changeCurrency = currencyCode => {
    setCurrencySelection(currencyCode);
    setChangedCurrency(true);
  };
  const saveChanges = async () => {
    if (changedCurrency) await updateUserCurrency();
    if (changedLanguage) {
      changePageLocale(languageSelection);
    }
    setIsOpen(false);
  };
  const updateUserCurrency = async () => {
    setIsUpdatingUserData(true);
    const updateResult = await customersManager.updateUserData({
      currency: currencySelection
    });
    setIsUpdatingUserData(false);
    if ("customer_user" in updateResult && updateResult.customer_user.currency === currencySelection) {
      updateCustomerStates(updateResult.customer_user);
      localStorage.setItem("currency", currencySelection);
      notify(t("CURRENCY_UPDATED"), NotificationType.SUCCESS);
      trackEvent(MIXPANEL_EVENTS.Profile.CHANGE_CURRENCY_SUCCESS);
    } else {
      notify(t("CURRENCY_UPDATE_ERROR"), NotificationType.ERROR);
      setCurrencySelection(customerCurrency !== null && customerCurrency !== void 0 ? customerCurrency : process.env.NEXT_PUBLIC_DEFAULT_CURRENCY || DEFAULT_CURRENCY);
      trackEvent(MIXPANEL_EVENTS.Profile.CHANGE_CURRENCY_ERROR);
    }
  };
  const handleConfigModalOpen = isOpen => {
    if (isOpen) {
      setIsLanguageDropdownOpen(false);
      setIsCurrencyDropdownOpen(false);
    }
    resetCurrencySelection();
    setIsOpen(isOpen);
  };
  const resetCurrencySelection = () => {
    setCurrencySelection(customerCurrency || process.env.NEXT_PUBLIC_DEFAULT_CURRENCY || DEFAULT_CURRENCY);
  };
  return __jsx(MuscleModal, {
    openState: isOpen,
    setOpenState: handleConfigModalOpen,
    panelClassName: "md:w-fit",
    dataTestId: "modal-view-config",
    title: getLanguageOptions().length === 1 ? t("CHANGE_CURRENCY") : t("TITLE")
  }, __jsx("div", {
    className: "flex flex-col w-full md:w-[450px] gap-4 items-center h-[300px]"
  }, getLanguageOptions().length > 1 && __jsx(MuscleSelectInput, {
    label: t("LANGUAGE"),
    options: getLanguageOptions(),
    onSelectOption: option => changeLanguage(option.key),
    value: getLanguageOptions().find(option => option.key === currentLanguageCode),
    dataTestId: "modal-config-language",
    isOpen: isLanguageDropdownOpen,
    onDropdownToggle: isOpen => {
      if (isOpen) setIsCurrencyDropdownOpen(false);
      setIsLanguageDropdownOpen(isOpen);
    }
  }), __jsx(MuscleSelectInput, {
    label: t("CURRENCY"),
    options: currencyOptions,
    onSelectOption: option => changeCurrency(option.key),
    value: currencyOptions.find(option => option.key === currencySelection),
    dataTestId: "modal-config-currency",
    isOpen: isCurrencyDropdownOpen,
    onDropdownToggle: isOpen => {
      if (isOpen) setIsLanguageDropdownOpen(false);
      setIsCurrencyDropdownOpen(isOpen);
    }
  }), __jsx(MuscleButton, {
    variant: "solidMd",
    onClick: saveChanges,
    isLoading: isUpdatingUserData,
    disabled: !changedLanguage && !changedCurrency,
    dataTestId: "modal-config-save-button"
  }, t("SAVE"))));
};
export default LanguageCurrencyConfigModal;